// app/composables/season/seasonFilter.ts

import { useCompetitionStore } from "~/app/stores/competitionStore";
import { useRouteQuery } from "~/app/composables/routeQuery";
import { $SEASON_CLUSTER_ROUTE_QUERY_NAME } from "~/src/season-cluster/infrastructure/ui-services/seasonClusterUiService";
import { $SEASON_ROUTE_QUERY_NAME } from "~/src/season-cluster/infrastructure/ui-services/seasonUiService";
import { $REGION_ROUTE_QUERY_NAME } from "~/src/region/infrastructure/ui-services/regionUiService";

interface UseSeasonFilter {
  seasonClusterId: Ref<number | undefined>;
  seasonId: Ref<number | undefined>;
  regionId: Ref<number | undefined>;
  changeSeason: (
    seasonClusterId: number,
    seasonId: number,
    competitionId?: number | undefined,
    initialValues?: {
      seasonId: number;
      seasonClusterId: number;
      regionId: number;
    }
  ) => void;
}

export const useSeasonFilter = (
  initialValues?: Record<string, number | undefined> | undefined
): UseSeasonFilter => {
  const { currentSeasonCluster, currentSeason } = useCompetitionStore();
  const { query } = useRoute();
  const { deleteQuery } = useRouteQuery();
  const seasonClusterId = ref<number | undefined>();
  const seasonId = ref<number | undefined>();
  const regionId = ref<number | undefined>();

  const routeSeasonClusterId = Number(query[$SEASON_CLUSTER_ROUTE_QUERY_NAME]);
  const routeSeasonId = Number(query[$SEASON_ROUTE_QUERY_NAME]);
  const routeRegionId = query[$REGION_ROUTE_QUERY_NAME] ? Number(query[$REGION_ROUTE_QUERY_NAME]) : undefined;

  const validQueryIds = routeRegionId
    ? !isNaN(routeSeasonId) &&
      !isNaN(routeSeasonClusterId) &&
      !isNaN(routeRegionId) &&
      validateSeasonAndSeasonClusterQuery(routeSeasonClusterId, routeSeasonId)
    : !isNaN(routeSeasonId) &&
      !isNaN(routeSeasonClusterId) &&
      validateSeasonAndSeasonClusterQuery(routeSeasonClusterId, routeSeasonId);

  if (validQueryIds) {
    seasonClusterId.value = routeSeasonClusterId;
    seasonId.value = routeSeasonId;
    regionId.value = routeRegionId;
  } else {
    if (!!initialValues) {
      deleteQuery([$SEASON_CLUSTER_ROUTE_QUERY_NAME, $SEASON_ROUTE_QUERY_NAME, $REGION_ROUTE_QUERY_NAME]);
      seasonClusterId.value = initialValues.seasonClusterId;
      seasonId.value = initialValues.seasonId;
      regionId.value = initialValues.regionId;
    } else {
      deleteQuery([$SEASON_CLUSTER_ROUTE_QUERY_NAME, $SEASON_ROUTE_QUERY_NAME, $REGION_ROUTE_QUERY_NAME]);
      seasonClusterId.value = currentSeasonCluster?.id;
      seasonId.value = currentSeason?.id;
      regionId.value = undefined;
    }
  }

  return {
    seasonClusterId,
    seasonId,
    regionId,
    changeSeason,
  };
};

function validateSeasonAndSeasonClusterQuery(seasonClusterId: number, seasonId: number): boolean {
  const { seasonClusters } = useCompetitionStore();
  const seasonCluster = seasonClusters.find(sc => sc.id === seasonClusterId);
  return !!seasonCluster?.seasons?.find(s => s.id === seasonId);
}

function changeSeason(
  seasonClusterId: number,
  seasonId: number,
  regionId?: number,
  initialValues?: Record<string, number | undefined> | undefined
) {
  const { currentSeasonCluster, currentSeason } = useCompetitionStore();
  const { deleteQuery, addQuery } = useRouteQuery();

  if (
    initialValues &&
    initialValues.seasonClusterId === seasonClusterId &&
    initialValues.seasonId === seasonId
  ) {
    deleteQuery([$SEASON_CLUSTER_ROUTE_QUERY_NAME, $SEASON_ROUTE_QUERY_NAME, $REGION_ROUTE_QUERY_NAME]);
  } else if (
    !initialValues &&
    currentSeasonCluster?.id === seasonClusterId &&
    currentSeason?.id === seasonId
  ) {
    deleteQuery([$SEASON_CLUSTER_ROUTE_QUERY_NAME, $SEASON_ROUTE_QUERY_NAME, $REGION_ROUTE_QUERY_NAME]);
  } else {
    const queries: Record<string, number> = {};

    queries[$SEASON_CLUSTER_ROUTE_QUERY_NAME] = seasonClusterId;
    queries[$SEASON_ROUTE_QUERY_NAME] = seasonId;
    if (regionId) {
      queries[$REGION_ROUTE_QUERY_NAME] = regionId;
    }

    addQuery(queries, { replace: true });
  }
}
